<template>
  <!-- Карточка «Быстрый расчёт» справа от «Онлайн-лизинга» (прототип V3):
       параметр — строка «подпись — значение» и ползунок под ней, срок —
       сегменты, внизу платёж и кнопка заявки. До первого расчёта карточка
       переливается целиком (st-skel-block: содержимое скрыто, размеры те же);
       при пересчётах прежняя сумма лишь бледнеет -->
  <div
    class="st-qcalc"
    :class="initialLoading && 'st-skel-block'"
    role="group"
    :aria-busy="initialLoading"
    aria-labelledby="calc-h"
  >
    <h3 id="calc-h" class="st-qcalc__title">Быстрый расчёт</h3>

    <!-- стоимость и аванс можно ввести с клавиатуры прямо в значении строки:
         в границах применяется сразу, при уходе из поля подтягивается к ним -->
    <div class="st-qslider">
      <div class="st-qslider__line">
        <label for="calc-price">Стоимость предмета лизинга</label>
        <span class="st-qslider__value">
          <span class="st-qslider__autosize" :data-value="priceText">
            <input
              id="calc-price"
              class="st-qslider__input"
              size="1"
              inputmode="numeric"
              autocomplete="off"
              :value="priceText"
              @input="onPriceInput"
              @blur="onPriceBlur"
              @keydown.enter="$event.target.blur()"
            /></span
          ><span class="st-qslider__unit">₽</span>
        </span>
      </div>
      <input
        class="st-range"
        type="range"
        aria-label="Стоимость предмета лизинга"
        :min="priceMin"
        :max="priceMax"
        :step="priceStep"
        :value="Math.min(Math.max(price, priceMin), priceMax)"
        :style="{ '--pct': pct(price, priceMin, priceMax) }"
        @input="price = Number($event.target.value)"
      />
    </div>

    <div class="st-qslider">
      <div class="st-qslider__line">
        <label for="calc-adv">Аванс</label>
        <!-- сумма аванса — перед процентом: жирные значения выровнены по правому краю -->
        <span class="st-qslider__value">
          <small>{{ rub((price * adv) / 100) }}</small
          ><span class="st-qslider__autosize" :data-value="advText">
            <input
              id="calc-adv"
              class="st-qslider__input"
              size="1"
              inputmode="numeric"
              autocomplete="off"
              :value="advText"
              @input="onAdvInput"
              @blur="commitAdv"
              @keydown.enter="$event.target.blur()"
            /></span
          >%
        </span>
      </div>
      <input
        class="st-range"
        type="range"
        aria-label="Аванс, %"
        :min="advMin"
        :max="advMax"
        step="1"
        :value="adv"
        :style="{ '--pct': pct(adv, advMin, advMax) }"
        @input="adv = Number($event.target.value)"
      />
    </div>

    <div class="st-qslider">
      <div class="st-qslider__line">
        <span id="calc-term-l">Срок лизинга</span>
        <span class="st-qslider__value">{{ term }} мес.</span>
      </div>
      <div class="st-seg" role="radiogroup" aria-labelledby="calc-term-l">
        <button
          v-for="value in terms"
          :key="value"
          class="st-seg__item"
          :class="value === term && 'is-active'"
          type="button"
          role="radio"
          :aria-checked="value === term"
          @click="term = value"
        >
          {{ value }}
        </button>
      </div>
    </div>

    <div v-if="error" class="st-qcalc__error">{{ error }}</div>

    <div class="st-qcalc__foot">
      <div
        class="st-qcalc__pay"
        :class="loading && 'is-loading'"
        aria-live="polite"
      >
        <span>Платёж в месяц</span>
        <b>{{ result ? rub(shownPay) : "—" }}</b>
      </div>
      <button
        class="st-btn st-btn--base st-btn--lg"
        type="button"
        :disabled="!result || loading"
        @click="requestOpen = true"
      >
        Оформить заявку
      </button>
    </div>
    <p class="st-note">Расчёт предварительный и не является офертой</p>

    <!-- «Оформить заявку»: общая форма заявки на лизинг, предзаполненная
         стоимостью, авансом и сроком; результат расчёта уходит менеджеру -->
    <ApplicationModal
      v-if="requestOpen"
      kind="calc"
      :initial="{
        PLPrice: String(price),
        advance: String(adv),
        term: String(term),
      }"
      :calc="{ payment: result.secondElement, total: result.totalSum }"
      @close="requestOpen = false"
    />
  </div>
</template>

<script>
import { quickCalc } from "@/views/catalog/api";
import ApplicationModal from "@/component/application/ApplicationModal.vue";
import { rub } from "../helpers";

// задержка перед запросом расчёта, пока клиент двигает ползунок
const CALC_DELAY = 400;

// длительность «прокрутки» суммы платежа к новому значению, мс
const ROLL_TIME = 400;

export default {
  name: "QuickCalc",
  components: { ApplicationModal },
  data() {
    return {
      price: 3000000,
      priceText: "3 000 000",
      // границы расчёта: стоимость 500 000 — 50 000 000 ₽, аванс 5–49%, срок 12–60 мес.
      priceMin: 500000,
      priceMax: 50000000,
      priceStep: 50000,
      // значения по умолчанию: 3 000 000 ₽, аванс 30%, срок 60 мес.
      adv: 30,
      // текст поля аванса: пока клиент набирает, значение не ограничивается —
      // проверка и применение после ввода (commitAdv)
      advText: "30",
      advMin: 5,
      advMax: 49,
      term: 60,
      terms: [12, 24, 36, 48, 60],
      result: null,
      // платёж на экране: плавно догоняет новый результат
      shownPay: 0,
      rollFrame: null,
      error: null,
      loading: false,
      timer: null,
      // защита от гонки ответов: показываем только последний расчёт
      requestToken: 0,
      // окно заявки по расчёту (общая форма заявки на лизинг)
      requestOpen: false,
    };
  },
  computed: {
    // первый расчёт ещё не пришёл — весь блок показывается скелетом
    initialLoading() {
      return this.loading && !this.result && !this.error;
    },
    params() {
      return {
        LeasingSubjectPriceBase: this.price,
        Deposit: this.adv,
        LeasingTerm: this.term,
        // график только равномерный
        PaymentPlanType: "eq",
      };
    },
  },
  watch: {
    params: {
      deep: true,
      handler() {
        this.schedule();
      },
    },
    adv(value) {
      this.advText = String(value);
    },
    "result.secondElement"(value) {
      this.roll(Number(value) || 0);
    },
    price(value) {
      // при вводе с клавиатуры поле форматирует onPriceInput/onPriceBlur
      if (document.activeElement?.id !== "calc-price") {
        this.priceText = this.format(value);
      }
    },
  },
  created() {
    this.calc();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.rollFrame);
    this.requestToken++;
  },
  methods: {
    rub,
    format(value) {
      return Number(value).toLocaleString("ru-RU");
    },
    pct(value, min, max) {
      const clamped = Math.min(Math.max(value, min), max);
      return ((clamped - min) / (max - min)) * 100 + "%";
    },
    clamp(value, min, max) {
      const num = Math.round(Number(value));
      if (isNaN(num)) return min;
      return Math.min(Math.max(num, min), max);
    },
    // только цифры, не длиннее max символов
    digits(value, max) {
      return String(value).replace(/\D/g, "").slice(0, max);
    },
    // ввод с клавиатуры: значение в границах применяется сразу (расчёт — с той
    // же задержкой, что у ползунка); промежуточное («3» на пути к «36») ждёт
    // продолжения ввода, а при уходе из поля подтягивается к границам
    onAdvInput(event) {
      this.advText = this.digits(event.target.value, 2);
      const value = Number(this.advText);
      if (this.advText && value >= this.advMin && value <= this.advMax) {
        this.adv = value;
      }
    },
    commitAdv() {
      this.adv = this.clamp(this.advText || this.advMin, this.advMin, this.advMax);
      this.advText = String(this.adv);
    },
    // стоимость с клавиатуры: как аванс — в границах применяется сразу,
    // иначе ждёт продолжения ввода; при уходе из поля подтягивается к границам
    onPriceInput(event) {
      const digits = event.target.value.replace(/\D/g, "").slice(0, 9);
      this.priceText = digits ? this.format(digits) : "";
      const value = Number(digits);
      if (value >= this.priceMin && value <= this.priceMax) this.price = value;
    },
    onPriceBlur() {
      const value = Number(this.priceText.replace(/\D/g, ""));
      this.price = Math.min(Math.max(value || this.priceMin, this.priceMin), this.priceMax);
      this.priceText = this.format(this.price);
    },
    // сумма платежа «докручивается» до нового значения; первый расчёт и
    // режим без анимации (prefers-reduced-motion) — сразу
    roll(to) {
      cancelAnimationFrame(this.rollFrame);
      const from = this.shownPay;
      const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!from || still) {
        this.shownPay = to;
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / ROLL_TIME);
        const eased = 1 - Math.pow(1 - t, 3);
        this.shownPay = Math.round(from + (to - from) * eased);
        if (t < 1) this.rollFrame = requestAnimationFrame(step);
      };
      this.rollFrame = requestAnimationFrame(step);
    },
    schedule() {
      clearTimeout(this.timer);
      this.loading = true;
      this.timer = setTimeout(this.calc, CALC_DELAY);
    },
    async calc() {
      const token = ++this.requestToken;
      this.loading = true;
      let response;
      try {
        response = await quickCalc(this.params);
      } catch (e) {
        response = null;
      }
      if (token !== this.requestToken) return;
      if (response && response.status) {
        this.result = response;
        this.error = null;
      } else {
        this.result = null;
        this.error =
          (response && response.error) ||
          "Не удалось выполнить расчёт. Попробуйте позже.";
      }
      this.loading = false;
    },
  },
};
</script>
